import { createContext, useContext, useMemo } from 'react';
import useCloseBottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/hooks/useCloseBottomDrawer';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

interface ResponsiveModalContextType {
	/**
	 * Closes the bottom drawer with a transition.
	 */
	closeDrawer: () => void;
	isDrawerClosing: boolean;
	isMobileScreenSize: boolean;
	/**
	 * The original callback passed to the provider. Typically causes an immediate close.
	 */
	onClose: () => void;
	/**
	 * A "responsive" close function, i.e. either closes the modal immediately
	 * or the bottom drawer with a transition.
	 *
	 * Typically useful when needing to close from a footer button.
	 */
	close: () => void;
}

const ResponsiveModalContext = createContext<ResponsiveModalContextType | null>(
	null
);

interface Props {
	children: React.ReactNode;
	onClose: () => void;
}

export default function ResponsiveModalProvider({ children, onClose }: Props) {
	const isMobileScreenSize = useIsMobileScreenSize();

	const { closeDrawer, isDrawerClosing } = useCloseBottomDrawer({
		onClose,
	});

	const value = useMemo(
		() => ({
			closeDrawer,
			isDrawerClosing,
			isMobileScreenSize,
			onClose,
			close: isMobileScreenSize ? closeDrawer : onClose,
		}),
		[closeDrawer, isDrawerClosing, isMobileScreenSize, onClose]
	);

	return (
		<ResponsiveModalContext.Provider value={value}>
			{children}
		</ResponsiveModalContext.Provider>
	);
}

export const useResponsiveModal = () => {
	const context = useContext(ResponsiveModalContext);

	if (!context) {
		throw new Error(
			'useResponsiveModal must be used within a ResponsiveModalProvider'
		);
	}

	return context;
};
